import { getFormErrorMessage } from "@core/utility/get-error-message";
import { VehicleTransactionFormRule, VehicleTransactionFormSchema } from "@factory/form/form.rule";
import { useFormDefaultVehicleTransaction } from "@factory/form/form-default-vehicle-transaction.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseVehicleTransactionModel } from "@factory/model/query-model-vehicle-transaction";
import { 
  useQyDeleteVehicleTransaction, 
  useQyGetVehicleTransactionById, 
  useQyUpdateVehicleTransaction} from "@factory/query/vehicle-transaction.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditVehicleTransaction() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultVehicleTransaction();
  const navigate = useNavigate();
  const { mapUpdateVehicleTransaction, mapDeleteVehicleTransaction } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { vehicleTransactionId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID VehicleTransaction
  const handleGetApiSuccess = (data: GetResponseVehicleTransactionModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("tenant", responseData?.tenant || "");
      setValue("vehicle", responseData?.vehicle || "");
      setValue("transaction", responseData?.transaction || "");
      setValue("userCode", responseData?.user_code || "");
  };
  const {
    data: vehicleTransactionResponse,
    isLoading,
    isError: vehicleTransactionError,
  } = useQyGetVehicleTransactionById(vehicleTransactionId || "", handleGetApiSuccess);
  const vehicleTransactionData = vehicleTransactionResponse?.data?.[0];

  // API UPDATE VehicleTransaction
  const handleUpdateApiSuccess = () => {
    showSuccess("VehicleTransaction updated successfully");
  };
  const { mutate: updateVehicleTransaction, isLoading: isEditLoading } = useQyUpdateVehicleTransaction(
    handleUpdateApiSuccess
  );

  // API DELETE VehicleTransaction
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("VehicleTransaction deleted successfully");
  };
  const { mutate: deleteVehicleTransaction } = useQyDeleteVehicleTransaction(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<VehicleTransactionFormSchema>({
    defaultValues: getDefault(vehicleTransactionData),
    resolver: zodResolver(VehicleTransactionFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: VehicleTransactionFormSchema) => {
    if (!vehicleTransactionData || !vehicleTransactionData.code) {
      throw new Error("No vehicleTransaction data");
    }

    const formData = mapUpdateVehicleTransaction(form, vehicleTransactionData.code);
    updateVehicleTransaction(formData);
  };
  const handleValidateError = (err: FieldErrors<VehicleTransactionFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!vehicleTransactionData || !vehicleTransactionData.code) {
      throw new Error("No vehicleTransaction data found");
    }

    const form = mapDeleteVehicleTransaction(vehicleTransactionData.code);
    deleteVehicleTransaction(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    vehicleTransactionId,
    vehicleTransactionData,
    isLoading,
    vehicleTransactionError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}